import { useState, FormEvent } from 'react';
import { validateClientCode } from '@me-get/shared';
import type { Company, CompanyCreatePayload, CompanyUpdatePayload } from '../services/companies';

interface CompanyFormProps {
  company?: Company | null;
  onSubmit: (data: CompanyCreatePayload | CompanyUpdatePayload) => Promise<void>;
  onCancel: () => void;
  isSubmitting?: boolean;
}

export function CompanyForm({ company, onSubmit, onCancel, isSubmitting = false }: CompanyFormProps) {
  const isEdit = !!company;

  const [usualName, setUsualName] = useState(company?.usual_name ?? '');
  const [clientCode, setClientCode] = useState(company?.client_code ?? '');
  const [domain, setDomain] = useState(company?.domain ?? '');
  const [errors, setErrors] = useState<Record<string, string>>({});

  function validate(): boolean {
    const newErrors: Record<string, string> = {};

    if (!usualName.trim()) {
      newErrors.usual_name = 'Usual name is required';
    } else if (usualName.trim().length > 120) {
      newErrors.usual_name = 'Usual name must not exceed 120 characters';
    }

    if (!clientCode.trim()) {
      newErrors.client_code = 'Client code is required';
    } else {
      const codeResult = validateClientCode(clientCode.trim());
      if (!codeResult.valid) {
        newErrors.client_code = codeResult.errors[0];
      }
    }

    if (!domain.trim()) {
      newErrors.domain = 'Domain is required';
    } else {
      const domainPattern = /^(?:[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?\.)+[a-z]{2,}$/i;
      if (!domainPattern.test(domain.trim())) {
        newErrors.domain = 'Domain must be a valid internet domain (e.g., acme.com.br)';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  }

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    if (!validate()) return;

    const payload = {
      usual_name: usualName.trim(),
      client_code: clientCode.trim(),
      domain: domain.trim(),
    };

    await onSubmit(payload);
  }

  return (
    <form className="form" onSubmit={handleSubmit} noValidate>
      <h2>{isEdit ? 'Edit Company' : 'Create Company'}</h2>

      <div className="form__field">
        <label htmlFor="usual_name">Usual Name *</label>
        <input
          id="usual_name"
          type="text"
          value={usualName}
          onChange={(e) => setUsualName(e.target.value)}
          maxLength={120}
          required
          aria-invalid={!!errors.usual_name}
          aria-describedby={errors.usual_name ? 'usual_name-error' : undefined}
        />
        {errors.usual_name && (
          <span id="usual_name-error" className="form__error" role="alert">
            {errors.usual_name}
          </span>
        )}
      </div>

      <div className="form__field">
        <label htmlFor="client_code">Client Code *</label>
        <input
          id="client_code"
          type="text"
          value={clientCode}
          onChange={(e) => setClientCode(e.target.value.toLowerCase())}
          maxLength={30}
          required
          placeholder="lowercase-alphanumeric-hyphens"
          aria-invalid={!!errors.client_code}
          aria-describedby={errors.client_code ? 'client_code-error' : undefined}
        />
        <span className="form__hint">1-30 characters, lowercase letters, numbers, and hyphens only</span>
        {errors.client_code && (
          <span id="client_code-error" className="form__error" role="alert">
            {errors.client_code}
          </span>
        )}
      </div>

      <div className="form__field">
        <label htmlFor="domain">Domain *</label>
        <input
          id="domain"
          type="text"
          value={domain}
          onChange={(e) => setDomain(e.target.value)}
          required
          placeholder="acme.com.br"
          aria-invalid={!!errors.domain}
          aria-describedby={errors.domain ? 'domain-error' : undefined}
        />
        {errors.domain && (
          <span id="domain-error" className="form__error" role="alert">
            {errors.domain}
          </span>
        )}
      </div>

      <div className="form__actions">
        <button
          type="submit"
          className="btn btn-primary"
          disabled={isSubmitting}
        >
          {isSubmitting ? 'Saving...' : isEdit ? 'Update' : 'Create'}
        </button>
        <button
          type="button"
          className="btn btn-secondary"
          onClick={onCancel}
          disabled={isSubmitting}
        >
          Cancel
        </button>
      </div>
    </form>
  );
}
